import { useEffect, useRef, useState } from 'react';

import type { ShowcaseHighlight } from '@/nova/mock-data/types';
import { SHOWCASE_ANIM } from '@/nova/theme/tokens';

/**
 * Drives the active showcase highlight's char-by-char description type-on. Feature-tier
 * highlights are title + description only (no source pills — that machinery is the
 * Players step's), so this is a focused port of the configurator's `useHighlightReveal`:
 * after a short pre-type delay it types the description out one character at a time,
 * then calls `onComplete`. On `instant` it shows the full text with no animation
 * (already-seen cards). Resets to empty when the card is inactive.
 */

/** Estimated time (ms) a viewer needs to READ a highlight's description (distinct from
 *  how fast it types on): chars × the reading pace. */
export const estimateReadMs = (highlight: ShowcaseHighlight): number =>
  highlight.text.length * SHOWCASE_ANIM.READ_CHAR_MS;

/** Autoplay dwell (ms) for a highlight: `(clip length + estimated read time) × multiplier`.
 *  A highlight with no clip counts its clip length as 0. This is both the delay before the
 *  next talking point opens and the duration the depleting gauge counts down. */
export const autoplayDwellMs = (highlight: ShowcaseHighlight): number => {
  const videoMs = highlight.videoDurationS != null ? highlight.videoDurationS * 1000 : 0;
  return (videoMs + estimateReadMs(highlight)) * SHOWCASE_ANIM.AUTO_ADVANCE_MULTIPLIER;
};

/** Promise that resolves after `ms`, rejecting if the signal aborts. */
const wait = (ms: number, signal: AbortSignal): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('aborted', 'AbortError'));
      return;
    }
    const timer = window.setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timer);
        reject(new DOMException('aborted', 'AbortError'));
      },
      { once: true },
    );
  });

export function useShowcaseReveal(
  highlight: ShowcaseHighlight | undefined,
  active: boolean,
  instant: boolean,
  onComplete?: () => void,
): string {
  const [text, setText] = useState('');
  // Keep the latest onComplete without re-running the effect on every render.
  const completeRef = useRef(onComplete);
  completeRef.current = onComplete;

  useEffect(() => {
    if (!active || !highlight) {
      setText('');
      return;
    }

    if (instant) {
      setText(highlight.text);
      completeRef.current?.();
      return;
    }

    const ctrl = new AbortController();
    const { signal } = ctrl;
    const full = highlight.text;

    const run = async (): Promise<void> => {
      setText('');
      await wait(SHOWCASE_ANIM.PRE_TYPE_MS, signal);
      let partial = '';
      for (const ch of full) {
        await wait(SHOWCASE_ANIM.DESC_CHAR_MS, signal);
        partial += ch;
        setText(partial);
      }
    };

    run()
      .then(() => completeRef.current?.())
      .catch(() => {
        /* aborted — will re-run on next open */
      });

    return () => ctrl.abort();
  }, [highlight, active, instant]);

  return text;
}
